import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';

import { of, Subject, Subscription, timer } from 'rxjs';
import { takeUntil, catchError, filter, switchMap } from 'rxjs/operators';

import { HhsgSharedConfigService } from '../../../services/config.service';
import { HhsgSharedHeaderMenuLink } from '../menu/header-menu.component';
import { HhsgSharedNavHeader } from '../../../models/nav-header.model';
import { HhsgSharedNavHeaderService } from '../../../services/nav-header.service';
import { HhsgSharedUserProfile } from '../../../models/user-profile.model';
import { HhsgSharedUserService } from '../../../services/user.service';
import { HhsgSharedNavMenuItem } from '../../../models/nav-menu-item.model';
import { HhsgSharedNavSection } from '../../../models/nav-section.model';
import { HhsgSharedSubNavLink, HhsgSharedSubNavService } from '../../../services/sub-nav.service';
import { HhsgSharedNotificationService } from '../../../services/notification-service';
import { ErrorUtil } from '../../../utilities/error.util';
import { HhsgSharedApplication } from '../../../models/application.model';
import { HhsgSharedClientAdminService } from '../../../services';
import { Constants } from '../../../utilities/constants.util';


@Component({
    selector: 'hhsg-header[hhsg-nav-header]',
    templateUrl: './nav-header.component.html',
    styleUrls: ['nav-header.component.scss']
})
export class HhsgSharedNavHeaderComponent implements OnInit, OnDestroy {
    @Input() headers: HhsgSharedNavHeader[];
    @Input() applications: HhsgSharedHeaderMenuLink[];
    @Input() isMobile: boolean;
    @Input() selectedHeader: HhsgSharedNavHeader;
    @Input() showMenuButton: boolean;

    isNavExpanded = false;
    @Output() toggleHamburgerMenuEvent = new EventEmitter<boolean>();

    user: HhsgSharedUserProfile = <HhsgSharedUserProfile>{};
    showMenu: boolean;
    selfClick: boolean;
    currUrl: string;
    portalHome: string;
    notificationCountSubscription: Subscription;
    notificationSubscription: Subscription;
    notificationCount: number;
    applicationAbbreviation: string;
    activeApplications: HhsgSharedApplication[] = [];
    activeApplicationId: number;

    public hasUserProfile: boolean;
    public logoRoute: string;

    public documentClickListener: any;
    private navHeaderSelectedSubscription: Subscription;
    private navMenuItemSelectedSubscription: Subscription;
    private _unsubscribe$ = new Subject<void>();

    constructor(
        public configService: HhsgSharedConfigService,
        public renderer: Renderer2,
        private userProfile: HhsgSharedUserService,
        public navHeaderService: HhsgSharedNavHeaderService,
        public subNavService: HhsgSharedSubNavService,
        public notificationService: HhsgSharedNotificationService,
        private _clientAdminService: HhsgSharedClientAdminService) {
        this.portalHome = configService.appSettings.portalHome;
        this.applicationAbbreviation = configService.appSettings.applicationAbbreviation;
    }

    async ngOnInit(): Promise<void> {
        this.showMenu = false;

        await this.userProfile.getUserProfile()
            .pipe(takeUntil(this._unsubscribe$))
            .subscribe((user: HhsgSharedUserProfile) => {
                this.user = user;
                this.hasUserProfile = this.userProfile != null && this.userProfile != undefined;

                if (this.applicationAbbreviation === Constants.HuronAcceleratorsAbbreviation) {
                    this.logoRoute = '/';
                }
                else {
                    this.logoRoute = this.portalHome;
                }
                this.notificationSubscriptionFunction();
            });

        this.navHeaderSelectedSubscription = this.navHeaderService.navHeaderSelected$
            .pipe(takeUntil(this._unsubscribe$))
            .subscribe((header: HhsgSharedNavHeader) => {
                this.setSelectedHeader(header);
            });

        this.navMenuItemSelectedSubscription = this.navHeaderService.navMenuItemSelected$
            .pipe(takeUntil(this._unsubscribe$))
            .subscribe((menuItem: HhsgSharedNavMenuItem) => {
                this.setSelectedMenuItem(menuItem);
            });
        this.notificationCountSubscription = this.notificationService.notificationCount$
            .pipe(takeUntil(this._unsubscribe$))
            .subscribe((notificationCount: number) => {
                this.notificationCount = notificationCount;
            });
        // A recursive function that calls itself every one min.
        // The timer and checke to see if any new notifictaion has been received.
        // This will add dely (in miliseconds) to prevent params undefined issue of user id and application id for the first time.
        
    }

    notificationSubscriptionFunction(){
        this.notificationSubscription = timer(0, 60000)
            .pipe(switchMap(() => {
                //let userApplication = this.user.userApplications.find(x=>x.abbreviation==this.applicationAbbreviation);
                return this.notificationService.getNotificationCount((this.user && this.user.userId) ? this.user.userId : 0, this.applicationAbbreviation)
                    .pipe(catchError(err => {
                        ErrorUtil.logError(err);
                        return of(undefined);
                    }));
            }),
                filter(data => data !== undefined))
            .subscribe(notificationCount => {
                this.notificationCount = notificationCount;
                ErrorUtil.logError("UserId: " + this.user.userId + ", ApplicationAbbreviation: " + this.applicationAbbreviation + ", Notification Count:" + this.notificationCount);
            });
    }

    ngOnDestroy(): void {
        this.unbindDocumentClickListener();

        if (this.navHeaderSelectedSubscription) {
            this.navHeaderSelectedSubscription.unsubscribe();
        }

        if (this.navMenuItemSelectedSubscription) {
            this.navMenuItemSelectedSubscription.unsubscribe();
        }

        if (this.notificationCountSubscription) {
            this.notificationCountSubscription.unsubscribe();
        }
        if (this.notificationSubscription) {
            this.notificationSubscription.unsubscribe();
        }

        this._unsubscribe$.next();
        this._unsubscribe$.complete();
    }

    private bindDocumentClickListener() {
        if (!this.documentClickListener) {
            this.documentClickListener = this.renderer.listen('document', 'click', () => {
                if (!this.selfClick) {
                    this.showMenu = false;
                    this.unbindDocumentClickListener();
                }
            });
        }
    }

    public headerLinkClicked(header: HhsgSharedNavHeader) {
        if (header) {
            this.setSelectedHeader(header);
        }
    }

    public navLinkClicked() {
        this.selfClick = false;
    }

    private setSelectedHeader(header: HhsgSharedNavHeader) {
        if (!this.headers || !header) {
            return;
        }

        // Clear any previous selected header marks.
        this.headers.filter(h => h.name != header.name).forEach(h => {
            h.iconClass = "";
            h.selected = false;
        });

        // Find the selected header.
        this.selectedHeader = this.headers.find(h => h.name == header.name);

        if (this.selectedHeader) {
            // Set the selected header as currently selected.
            this.selectedHeader.iconClass = "icon-checkmark";
            this.selectedHeader.selected = true;
        }
    }

    private setSelectedMenuItem(menuItem: HhsgSharedNavMenuItem) {
        if (!this.selectedHeader) {
            return;
        }

        // Clear the sub nav.
        this.subNavService.setSubNavData({
            title: '',
            links: []
        });

        let links: HhsgSharedSubNavLink[] = [];
        let selectedSection: HhsgSharedNavSection;

        // Find the section that the selected menu item belongs to.
        this.selectedHeader.sections.forEach(s => {
            if (!selectedSection && s.items.find(m => m.route == menuItem.route)) {
                selectedSection = s;
            }
        });

        // If a selected section was found then rebuild the subnav.
        if (selectedSection) {
            selectedSection.items.forEach(m => {
                links.push({ href: m.route, name: m.subNavName })
            });

            this.subNavService.setSubNavData({
                title: selectedSection.name,
                links: links
            });
        }
    }

    public toggleMenu(): void {
        this.showMenu = !this.showMenu;
        this.selfClick = this.showMenu;

        if (this.showMenu) {
            this.bindDocumentClickListener();
        }
        else {
            this.unbindDocumentClickListener();
        }
    }

    private unbindDocumentClickListener() {
        if (this.documentClickListener) {
            this.documentClickListener();
            this.documentClickListener = null;
        }
    }

    public toggleNavExpanded(): void {
        this.isNavExpanded = !this.isNavExpanded;
        this.toggleHamburgerMenuEvent.emit(this.isNavExpanded);
    }
}
